/**
 * Native TypeScript utilities that replace the legacy `window.knocking` globals.
 *
 * These are ported from assets/knocking-library.js so the new reactive components
 * have no runtime dependency on the legacy script and no initialisation-timing issues.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface KnockingVariant {
  id: number;
  title: string;
  price: number;
  compare_at_price: number | null;
  available: boolean;
  featured_image: { src: string } | null;
}

export interface KnockingProduct {
  id: number;
  handle: string;
  title: string;
  tags: string[];
  featured_image: string;
  variants: KnockingVariant[];
}

export interface ShippingDate {
  longDate: string;
  shortDate?: string;
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

function addDays(days: number): Date {
  return new Date(Date.now() + days * 86_400_000);
}

function addBusinessDays(days: number): Date {
  const date = new Date();
  for (let added = 0; added < days;) {
    date.setDate(date.getDate() + 1);
    const dow = date.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return date;
}

const DATE_FMT_LONG: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  year: "numeric",
};
const DATE_FMT_SHORT: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
};

function formatShippingDate(date: Date): ShippingDate {
  return {
    longDate: date.toLocaleDateString("en-US", DATE_FMT_LONG),
    shortDate: date.toLocaleDateString("en-US", DATE_FMT_SHORT),
  };
}


// ─── Public API ───────────────────────────────────────────────────────────────

export function calculateShippingDate(tag: string): ShippingDate {
  if (tag.includes("businessdays")) {
    const n = parseInt(tag.replace("businessdays", ""), 10);
    return formatShippingDate(addBusinessDays(n));
  }
  if (tag.includes("days")) {
    const n = parseInt(tag.replace("days", ""), 10);
    return formatShippingDate(addDays(n));
  }
  // Absolute date: "YYYY-MM-DD"
  const [y, m, d] = tag.split("-").map(Number);
  return formatShippingDate(new Date(y, m - 1, d));
}

/**
 * Parse a `ships_by-*` product tag into human-readable shipping date strings.
 * Falls back to the `defaultShippingDate` theme setting when no tag is found.
 * Returns null when no default is configured either.
 */
export const getTagShippingDate = (tags: string[]): ShippingDate | null => {
  const fallback = (window as any).defaultShippingDate as string | undefined;

  if (!tags?.length) {
    return fallback ? calculateShippingDate(fallback) : null;
  }

  const shippingTag = tags.find((t) => t.includes("ships_by-"));
  if (!shippingTag) {
    return fallback ? calculateShippingDate(fallback) : null;
  }

  const trimmed = shippingTag.replace("ships_by-", "");

  if (trimmed.includes(":")) {
    const [a, b] = trimmed.split(":").map(calculateShippingDate);
    return {
      longDate: `${a.longDate} - ${b.longDate}`,
      shortDate: `${a.shortDate} - ${b.shortDate}`,
    };
  }

  return calculateShippingDate(trimmed);
};

/** Fetch a full Shopify product object by its URL handle. Returns null if not found. */
export const getProductByHandle = async (
  handle: string
): Promise<KnockingProduct | null> => {
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
};

/**
 * Format a price in cents to a dollar string (e.g. 1999 → "$19.99").
 * Omits trailing zeros so whole-dollar amounts render as "$50" not "$50.00".
 */
export const formatToDollarCurrencyString = (priceCents: number): string => {
  if (!priceCents) return "$";
  const str = priceCents.toString();
  const dollars = str.slice(0, -2) || "0";
  const cents = str.slice(-2).padStart(2, "0");
  if (cents === "00") return `$${dollars}`;
  return `$${dollars}.${cents}`;
};

/** Read a browser cookie by name. Returns empty string if not found. */
export const getCookie = (name: string): string => {
  const prefix = name + "=";
  for (const raw of document.cookie.split(";")) {
    const c = raw.trim();
    if (c.startsWith(prefix)) {
      try {
        return decodeURIComponent(c.slice(prefix.length));
      } catch {
        return c.slice(prefix.length);
      }
    }
  }
  return "";
};

/** Write a browser cookie. Defaults to a 7-day lifetime, root path. */
export const setCookie = (name: string, value: string, days = 7): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
};

/** Convert a string to a Shopify-style URL handle (lowercase, hyphenated). */
export const handleize = (str: string): string => {
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-$/, "")
    .replace(/^-/, "");
};

/**
 * "Change variant" affordance behaviour, page-dependent:
 *  - Product page: smooth-scroll so the variant picker sits at the bottom of the viewport.
 *  - Any other page (e.g. collection): no-op.
 *
 * Used by the sticky add-to-cart bar and the extended-offer modal so their
 * "Change" buttons behave consistently.
 */
export const scrollToVariantPicker = (): void => {
  const template = document.body.dataset.template ?? "";
  if (!template.startsWith("product")) return; // collection & elsewhere: do nothing
  const picker = document.querySelector<HTMLElement>(
    ".product-form__action-row-variant"
  );
  if (!picker) return;
  const top = Math.max(
    0,
    picker.getBoundingClientRect().bottom + window.scrollY - window.innerHeight
  );
  window.scrollTo({ top, behavior: "smooth" });
};

/**
 * Ask the Shopify CDN for an image at a given render width.
 *
 * Raw `featured_image` / `variant.featured_image.src` values come back at the
 * original upload size — commonly 2000x2000 — so dropping one into an `<img>`
 * unchanged ships ~100KB to paint a thumbnail. Pass the CSS width the image is
 * rendered at, doubled for retina.
 *
 * Non-Shopify and already-sized URLs are returned untouched.
 */
export const shopifyImageUrl = (
  src: string | null | undefined,
  width: number
): string => {
  if (!src || !width) return src ?? "";
  try {
    const url = new URL(src, window.location.origin);
    if (!/(^|\.)shopify\.com$/.test(url.hostname) && url.hostname !== window.location.hostname) {
      return src; // third-party CDN — no size param to add
    }
    url.searchParams.set("width", String(Math.round(width)));
    return url.toString();
  } catch {
    return src;
  }
};

/**
 * Append (or replace) a `variant` query param on a product URL, preserving any
 * existing query string.
 *
 * Recommendation ("You may also like") URLs already carry Shopify tracking params
 * (e.g. `?pr_prod_strat=…&pr_seq=uniform`), so a naive `` `${base}?variant=…` ``
 * would produce a malformed double-`?` URL and the variant would never be parsed.
 * Using the URL API keeps the existing params and joins with `&`.
 */
export const buildVariantUrl = (
  base: string,
  variantId: number | null | undefined
): string => {
  if (!variantId || !base || base === "#") return base;
  const url = new URL(base, window.location.origin);
  url.searchParams.set("variant", String(variantId));
  return url.pathname + url.search + url.hash; // keep it relative
};
